import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';
import {NotificationType} from '~core/enum';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {noWhitespaceCharacters} from '~shared/directives/form-validators';
import {Customer} from '~core/models';
import {CustomerService, DefaultNotificationService} from '~core/services';
import {CustomerOverviewTranslationsService} from '~shared/translations';
import {isPastDateDirective} from '~shared/directives/form-validators/is-past-date.directive';

/**
 * Workspace sidebar for editing customer data
 */
@UntilDestroy()
@Component({
  selector: 'app-customer-data',
  templateUrl: './customer-data.component.html',
  styleUrls: ['../../../customer-overview.component.scss']
})
export class CustomerDataComponent implements OnInit, OnDestroy {
  @Input() public customer: Customer;
  public updateCustomerForm: UntypedFormGroup;
  public spinnerName: string = 'customerDataSubmit';

  constructor(
    private customerOverviewTranslations: CustomerOverviewTranslationsService,
    private formBuilder: UntypedFormBuilder,
    private notificationService: DefaultNotificationService,
    private customerService: CustomerService,
    public flTranslations: FormLabelTranslationService,
    public buttonsTranslations: ButtonsTranslations,
    public spinnerService: SpinnerService,
    public customerOverviewService: CustomerOverViewService,
  ) {
  }

  public ngOnInit(): void {
    this.fillCustomerForm();
  }

  public ngOnDestroy(): void {
  }

  /**
   * Create the customer form.
   */
  public fillCustomerForm(event?: any): void {
    if (this.customer.isPerson) {
      this.updateCustomerForm = this.formBuilder.group({
        id: [this.customer.id],
        firstName: [this.customer.firstName, [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
          noWhitespaceCharacters
        ]],
        lastName: [this.customer.lastName, [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(200),
          noWhitespaceCharacters
        ]],
        birthday: [this.customer.birthday?.dateIso, [isPastDateDirective]],
        salutation: [this.customer.salutation.value],
        title: [this.customer.title, [
          noWhitespaceCharacters,
          Validators.maxLength(20)
        ]],
        company: [this.customer.company, [
          Validators.maxLength(150), noWhitespaceCharacters
        ]],
        externalAuthorization: [{value: this.customer.externalAuthorization, disabled: true}]
      });
    } else {
      this.updateCustomerForm = this.formBuilder.group({
        id: [this.customer.id],
        firstName: [this.customer.firstName, [
          Validators.minLength(2),
          Validators.maxLength(50),
          noWhitespaceCharacters
        ]],
        lastName: [this.customer.lastName, [
          Validators.minLength(2),
          Validators.maxLength(200),
          noWhitespaceCharacters
        ]],
        birthday: [this.customer.birthday?.dateIso, [isPastDateDirective]],
        salutation: [this.customer.salutation.value],
        title: [this.customer.title, [
          noWhitespaceCharacters,
          Validators.maxLength(20)
        ]],
        company: [this.customer.company, [
          Validators.required,
          Validators.maxLength(150),
          noWhitespaceCharacters
        ]],
        externalAuthorization: [{value: this.customer.externalAuthorization, disabled: true}]
      });
    }
  }

  /**
   * If customer form is valid update the customer, otherwise display the errors.
   */
  public updateFormSubmit(form: UntypedFormGroup): void {
    if (form.valid) {
      this.spinnerService.show(this.spinnerName);
      if (!form.value.birthday) {
        form.value.birthday = null;
      }
      this.customerService.update(form.value).pipe(untilDestroyed(this))
        .subscribe({
          next: (data: any) => {
            this.customerService.setSelectedCustomer(data);
            this.spinnerService.hide(this.spinnerName);
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.customerOverviewTranslations.i18nSuccessCustomerUpdate
            });
            this.customerOverviewService.toggleAllModal(false);
            this.fillCustomerForm();
          },
          error: (error: Error) => {
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.customerOverviewTranslations.i18nErrorCustomerUpdate
            });
            this.spinnerService.hide(this.spinnerName);
          }
        });
    }
  }

  public toggleCustomerType() {
    this.customerService.toggleCustomerType(this.customer.id).pipe(untilDestroyed(this))
      .subscribe({
        next: el => {
          location.reload();
        },
        error: err => {
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.customerOverviewTranslations.i18nErrorCustomerTypeChange
          });
        }
      });
  }
}
